import { useState } from 'react';

import { Flex, HStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { useDateRangeQueryState } from '@/components/Filters/Date/hooks';
import {
  RANGE_MAX_VALUE,
  RANGE_MIN_VALUE,
  getRangeScoreFrom,
  getRangeScoreTo,
} from '@/components/Filters/Range/RangeFilterComponent';
import { useRangeQueryState } from '@/components/Filters/Range/hooks';
import {
  type RatingObj,
  ratingItems,
} from '@/components/Filters/Rating/RatingFilterComponent';
import {
  FullScopePagination,
  type FullScopePaginationPageSizeType,
  fullScopePaginationPageSizes,
} from '@/components/Pagination/FullScopePagination';
import { dateRangeDefaultValue } from '@/components/Popover';
import {
  useAdminInterviewPageParams,
  useShareToken,
} from '@/features/admin-interview';
import {
  CandidateList,
  CandidateListHeaderSection,
  CandidateListSearchSection,
} from '@/features/candidate';
import { type StatusItemsObj } from '@/features/candidate/CandidatesFilters';
import {
  type CandidateBasicDto,
  CandidateListType,
  CandidateSortColumn,
  CustomSortOrder,
  type InterviewStatus,
} from '@/services/api';
import { statusFilterItems } from '@/utils/statusFilterItems';
import {
  parseAsInteger,
  parseAsJson,
  parseAsNumberLiteral,
  parseAsString,
  parseAsStringEnum,
  useQueryState,
} from '@/utils/useQueryState';

import {
  adminInterviewKeys,
  useGetInterviewCandidatesQuery,
} from './useAdminInterviewQueries';

const defaultRange = [RANGE_MIN_VALUE, RANGE_MAX_VALUE];

const statusDefaultValue = statusFilterItems.reduce((a, c) => {
  a[c.name] = false;
  return a;
}, {} as StatusItemsObj);

const ratingDefaultValue = ratingItems.reduce((a, c) => {
  a[c] = false;
  return a;
}, {} as RatingObj);

export const InterviewTable = () => {
  const { interviewId } = useAdminInterviewPageParams();
  const shareToken = useShareToken();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions({ clearOnDefault: true })
  );

  const [page, setPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({
      clearOnDefault: true,
    })
  );

  const [pageSize, setPageSize] =
    useQueryState<FullScopePaginationPageSizeType>(
      'pageSize',
      parseAsNumberLiteral(fullScopePaginationPageSizes)
        .withDefault(10)
        .withOptions({ clearOnDefault: true })
    );

  const [sortOrder, setSortOrder] = useQueryState(
    'sortOrder',
    parseAsStringEnum<CustomSortOrder>(
      Object.values(CustomSortOrder)
    ).withOptions({ clearOnDefault: true })
  );

  const [sortItem, setSortItem] = useQueryState(
    'sortItem',
    parseAsStringEnum<CandidateSortColumn>(
      Object.values(CandidateSortColumn)
    ).withOptions({ clearOnDefault: true })
  );

  const [emailSearchTerm, setEmailSearchTerm] = useQueryState(
    'email',
    parseAsString.withDefault('').withOptions({ clearOnDefault: true })
  );

  const [dateRangeSearchTerm, setDateRangeSearchTerm] =
    useDateRangeQueryState();

  const [rangeSearchTerm, setRangeSearchTerm] =
    useRangeQueryState(defaultRange);

  const [status, setStatus] = useQueryState<StatusItemsObj>(
    'status',
    parseAsJson<StatusItemsObj>()
      .withDefault(statusDefaultValue)
      .withOptions({ clearOnDefault: true })
  );

  const [rating, setRating] = useQueryState<RatingObj>(
    'rating',
    parseAsJson<RatingObj>()
      .withDefault(ratingDefaultValue)
      .withOptions({ clearOnDefault: true })
  );

  const [candidateListType, setCandidateListType] =
    useQueryState<CandidateListType>(
      'listType',
      parseAsStringEnum<CandidateListType>(Object.values(CandidateListType))
        .withDefault(CandidateListType.AllCandidates)
        .withOptions({ clearOnDefault: true })
    );

  const params = {
    interviewsId: interviewId!,
    size: pageSize,
    page,
    q: searchTerm || emailSearchTerm,
    aiScoreFrom: getRangeScoreFrom(rangeSearchTerm),
    aiScoreTo: getRangeScoreTo(rangeSearchTerm),
    dateFrom: dateRangeSearchTerm.from,
    dateTo: dateRangeSearchTerm.to,
    status: Object.keys(status)
      .filter((k) => status[k as InterviewStatus])
      .map((k) => k as InterviewStatus),
    ratings: Object.keys(rating)
      .filter((k) => rating[k as (typeof ratingItems)[number]])
      .map((k) => Number(k)),
    sortColumn: sortItem ?? undefined,
    sortOrder: sortOrder ?? undefined,
    candidateListType,
  };

  const candidatesQuery = useGetInterviewCandidatesQuery(params, {
    axiosRequestConfig: {
      overrideSecurityToken: shareToken,
    },
  });

  const [selectedCandidates, setSelectedCandidates] = useState<
    Array<CandidateBasicDto>
  >([]);
  const handleCandidateSelection = (
    candidate: CandidateBasicDto,
    isChecked: boolean
  ) => {
    setSelectedCandidates((prevSelected) => {
      const currentSelected = prevSelected ?? [];

      if (isChecked) {
        return [...currentSelected, candidate];
      } else {
        return currentSelected.filter((c) => c.id !== candidate.id);
      }
    });
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      const allCandidates = candidatesQuery.data?.data;
      setSelectedCandidates(allCandidates!);
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleClearSelection = () => {
    setSelectedCandidates([]);
  };

  const clearAllFilters = () => {
    setEmailSearchTerm('');
    setDateRangeSearchTerm(dateRangeDefaultValue);
    setRangeSearchTerm(defaultRange);
    setStatus(statusDefaultValue);
    setRating(ratingDefaultValue);

    setSortItem(null);
    setSortOrder(null);
  };

  const totalItems: number = candidatesQuery.data?.total ?? 0;

  return (
    <Flex direction="column" borderRadius="xl" bg="white">
      <CandidateListHeaderSection
        totalCandidates={totalItems}
        interviewId={interviewId ?? ''}
        searchTerm={searchTerm}
        rangeSearchTerm={rangeSearchTerm}
        status={status}
        rating={rating}
        dateRangeSearchTerm={dateRangeSearchTerm}
        candidateListType={candidateListType}
        setCandidateListType={setCandidateListType}
      />
      <CandidateListSearchSection
        interviewId={interviewId ?? ''}
        selectedCandidates={selectedCandidates}
        onClearSelection={handleClearSelection}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        emailSearchTerm={emailSearchTerm}
        setEmailSearchTerm={setEmailSearchTerm}
        rangeSearchTerm={rangeSearchTerm}
        setRangeSearchTerm={setRangeSearchTerm}
        status={status}
        setStatus={setStatus}
        rating={rating}
        setRating={setRating}
        dateRangeSearchTerm={dateRangeSearchTerm}
        setDateRangeSearchTerm={setDateRangeSearchTerm}
        totalCandidates={totalItems}
        setPage={setPage}
        clearAllFilters={clearAllFilters}
      />
      <CandidateList
        setSelectedCandidates={setSelectedCandidates}
        selectedCandidates={selectedCandidates}
        onCandidateSelect={handleCandidateSelection}
        onSelectAll={handleSelectAll}
        candidatesQuery={candidatesQuery}
        interviewId={interviewId ?? ''}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        sortItem={sortItem}
        setSortItem={setSortItem}
        onDeleteSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: adminInterviewKeys.getAllInterviewsCandidates(params),
            exact: false,
          });
        }}
      />

      <HStack py={4} px={6} justify="end">
        <FullScopePagination
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          totalItems={totalItems as number}
        />
      </HStack>
    </Flex>
  );
};
